import { useState } from 'react';
import { Button, Checkbox, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

/** The Complete Shipment request again, now choosing to take the not-loaded boxes' pieces out. */
export const withTakeOut = (payload) => ({ ...payload, data: { ...payload.data, not_loaded: 'take_out' } });

/**
 * PGSS-5842: the Complete Shipment request again, completing with the not-loaded boxes left as they are. The server
 * checks the setting again and refuses it when off.
 */
export const withCompleteAnyway = (payload) => ({ ...payload, data: { ...payload.data, not_loaded: 'complete_anyway' } });

/** PGSS-5842: the one confirmation before completing without scanning. */
export const completeAnywayQuestion = (count) =>
  `${count} ${count === 1 ? 'box was' : 'boxes were'} not scanned as loaded. Complete anyway?`;

/** The supplier's Scan to load screen for the container being completed. */
export const notLoadedScanPath = (payload) => `/shipments/scan-to-load/${payload.data.shipment_id}`;

/** One line per order: what comes off, what stays, and whether the order is split. Used on screen and on paper. */
export const outcomeLines = (orders = []) =>
  orders.flatMap((o) => [o.summary, ...(o.lines || []).map((line) => `  ${line}`)]);

/**
 * What came off at Complete Shipment, for the completion message: the supplier must pull these boxes out of the
 * container before it leaves, or they ship anyway.
 */
export const takenOutMessage = (res) => {
  const orders = res?.takenOutOrders || [];
  if (!orders.length) return null;
  return `Shipment completed. Remove these from the container before it leaves: ${orders
    .map((o) => [o.summary, ...(o.lines || [])].join(' — '))
    .join('. ')}.`;
};

const escapeHtml = (text) =>
  String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** A printable page listing the boxes to pull out, for the person at the container. */
export const printableList = (response) => {
  const rows = (response?.notLoadedBoxes || [])
    .map((b) => `<tr><td>${escapeHtml(b.orderNo)}</td><td>${escapeHtml(b.boxNumber)}</td><td>${escapeHtml(b.product)}</td><td>${escapeHtml(b.displayCode)}</td></tr>`)
    .join('');
  const outcomes = outcomeLines(response?.notLoadedOrders).map((l) => `<li>${escapeHtml(l.trim())}</li>`).join('');
  return `<html><head><title>Boxes to take out</title></head><body>
<h2>Boxes to take out of the container</h2>
<ul>${outcomes}</ul>
<table border="1" cellpadding="6" cellspacing="0"><tr><th>Order</th><th>Box</th><th>Product</th><th>Box code</th></tr>${rows}</table>
</body></html>`;
};

const printList = (response) => {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(printableList(response));
  win.document.close();
  win.focus();
  win.print();
};

/**
 * PGSS-5790: Complete Shipment found boxes not scanned as loaded. PSG's rule: a piece that was not loaded comes out
 * of the container and back to In Production, so it can go on another container. The supplier either goes back to
 * Scan to load, or takes those pieces out and completes the shipment with the rest.
 *
 * Taking out works like the Unload screen: an order none of whose pieces were loaded comes off whole; one with some
 * pieces loaded is split, and the pieces that came off go into a new order. The dialog says which, per order, and the
 * take-out waits until the supplier confirms the boxes are physically out of the container: otherwise they leave the
 * system but still travel, and end up delivered.
 *
 * When the backend can't take them out (nothing was scanned at all, or a line's boxes don't match its pieces) it says
 * why, and only Scan them now is offered.
 *
 * PGSS-5842: while suppliers change over to box labels, a setting lets the shipment complete with the boxes left as
 * they are. The backend says when it is on (canCompleteWithoutScanning); then Complete without scanning is offered,
 * also when the take-out is refused, and asks once before it completes.
 */
export default function NotLoadedBoxesDialog({ response, onScan, onTakeOut, onCompleteAnyway, onCancel }) {
  const [removed, setRemoved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const boxes = response?.notLoadedBoxes || [];
  const orders = response?.notLoadedOrders || [];
  const canTakeOut = response?.canTakeOut === true;
  const canCompleteAnyway = response?.canCompleteWithoutScanning === true && Boolean(onCompleteAnyway);
  if (confirming) {
    return (
      <>
        <DialogTitle>Complete without scanning</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="not-loaded-complete-anyway-question">
            {completeAnywayQuestion(boxes.length)}
          </DialogContentText>
          <DialogContentText className="mt-12">
            The boxes stay on the container and nothing is taken out. PSG will see them as not loaded when the
            container is received.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} color="primary" data-testid="not-loaded-complete-anyway-back">
            Back
          </Button>
          <Button
            onClick={onCompleteAnyway}
            color="secondary"
            variant="contained"
            data-testid="not-loaded-complete-anyway-confirm"
          >
            Complete anyway
          </Button>
        </DialogActions>
      </>
    );
  }
  return (
    <>
      <DialogTitle>Boxes not loaded</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="not-loaded-message">{response?.message}</DialogContentText>
        <Table size="small" data-testid="not-loaded-boxes">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Box</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Box code</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boxes.map((box) => (
              <TableRow key={box.code}>
                <TableCell>{box.orderNo}</TableCell>
                <TableCell>{box.boxNumber}</TableCell>
                <TableCell>{box.product}</TableCell>
                <TableCell>{box.displayCode}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {canTakeOut && (
          <>
            <Typography className="mt-16 font-semibold">If you take them out:</Typography>
            <ul data-testid="not-loaded-outcomes" className="list-disc pl-24">
              {orders.map((o) => (
                <li key={o.orderNo}>
                  {o.summary}
                  {(o.lines || []).length > 0 && (
                    <ul className="list-[circle] pl-24">
                      {o.lines.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <DialogContentText className="mt-12">
              Pieces taken out go back to In Production with no container, so they can be added to another container.
              The shipment then completes with the boxes that were loaded.
            </DialogContentText>
            <FormControlLabel
              className="mt-12"
              control={
                <Checkbox
                  checked={removed}
                  onChange={(e) => setRemoved(e.target.checked)}
                  inputProps={{ 'data-testid': 'not-loaded-removed' }}
                />
              }
              label="I have physically removed these boxes from the container"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">Cancel</Button>
        <Button onClick={() => printList(response)} color="primary" data-testid="not-loaded-print">Print list</Button>
        <Button onClick={onScan} color="primary" data-testid="not-loaded-scan">Scan them now</Button>
        {canCompleteAnyway && (
          <Button onClick={() => setConfirming(true)} color="primary" data-testid="not-loaded-complete-anyway">
            Complete without scanning
          </Button>
        )}
        {canTakeOut && (
          <Button
            onClick={onTakeOut}
            disabled={!removed}
            color="secondary"
            variant="contained"
            data-testid="not-loaded-take-out"
          >
            Take them out and complete
          </Button>
        )}
      </DialogActions>
    </>
  );
}
